import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Ticket, ExternalLink } from "lucide-react";

const STATUSES = ["ouvert", "en cours", "résolu"];
const STATUS_STYLE = {
  ouvert: { c: "#EAB308", bg: "#EAB30818", b: "#EAB30833" },
  "en cours": { c: "#3b82f6", bg: "#3b82f618", b: "#3b82f633" },
  "résolu": { c: "#22c55e", bg: "#22c55e18", b: "#22c55e33" },
};

const PRIO_COLOR = { basse: "#888", normale: "#EAB308", haute: "#f59e0b", urgente: "#dc143c" };

export default function SupportTicketsPage() {
  const { t: tr } = useI18n();
  const [tickets, setTickets] = useState([]);

  const load = () => api.get("/support/tickets").then(({ data }) => setTickets(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => {
    await api.patch(`/support/tickets/${id}`, { status });
    setTickets((ts) => ts.map((t) => (t.id === id ? { ...t, status } : t)));
    toast.success(tr("dash.pages.tickets.updated"));
  };

  return (
    <div className="space-y-8" data-testid="support-tickets-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold text-white">{tr("dash.pages.tickets.title")}</h1>
          <p className="mt-1 text-white/55">{tr("dash.pages.tickets.desc")}</p>
        </div>
        <Link
          to="/support"
          data-testid="open-support-center"
          className="inline-flex items-center gap-2 rounded-md border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white hover:border-[#EAB308]/50 transition-colors"
        >
          <ExternalLink className="w-4 h-4" /> {tr("dash.pages.tickets.openCenter")}
        </Link>
      </div>

      {tickets.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-[#0a0a0a] p-10 text-center text-white/40">
          <Ticket className="w-8 h-8 mx-auto mb-3 opacity-40" />
          {tr("dash.pages.tickets.empty")}
          <div className="mt-4">
            <Link to="/support" className="text-[#EAB308] hover:underline text-sm">{tr("dash.pages.tickets.openTicket")}</Link>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((t) => {
            const s = STATUS_STYLE[t.status] || STATUS_STYLE.ouvert;
            return (
              <div key={t.id} className="rounded-lg border border-white/10 bg-[#0a0a0a] p-5" data-testid="ticket-row">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-[#EAB308]">{t.ref}</span>
                      <span className="font-semibold text-white">{t.subject}</span>
                      <span className="text-[11px] uppercase font-mono" style={{ color: PRIO_COLOR[t.priority] || "#888" }}>
                        • {tr(`dash.pages.tickets.prio.${t.priority}`)}
                      </span>
                    </div>
                    <p className="mt-1.5 text-sm text-white/55 line-clamp-2">{t.message}</p>
                    <p className="mt-1 text-xs text-white/30">{t.name} — {t.email}</p>
                  </div>
                  <select
                    data-testid={`ticket-status-${t.ref}`}
                    value={t.status}
                    onChange={(e) => changeStatus(t.id, e.target.value)}
                    className="h-9 rounded-md border px-3 text-sm capitalize"
                    style={{ background: s.bg, borderColor: s.b, color: s.c }}
                  >
                    {STATUSES.map((st) => <option key={st} value={st} className="bg-[#0a0a0a] text-white capitalize">{tr(`dash.pages.tickets.statuses.${st}`)}</option>)}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
